/**
 * C-GTCC-VIEW-OVW1 — typed read client for the GTCC service endpoints.
 *
 * The shapes below are the data contract of K-GTCC-API-SVC1, transcribed from
 * the C-instruction body without addition: `svc/services`,
 * `svc/delivery-watchdog` and `rules/summary`. K owns the server side; until K
 * is merged the views render fixtures (`../fixtures/svc.*.json`,
 * `../fixtures/rules.summary.json`) that carry the values of
 * `GTCC_REFERENCE_2026-09-01.html`.
 *
 * Read-only by construction: `gtccGet` wraps `apiGet` only.
 *
 * `rules/summary` carries the CRITICAL ALERTS number of the Overview: the
 * instruction defines that counter as *firing crit*, not as configured rules.
 */
import {
  gtccGet,
  type GtccMeasured,
  type GtccResult,
  type GtccStatus,
  type GtccVerdict,
} from "./gtcc-result.js";

/* --------------------------------------------------------------- paths */

const SVC_BASE = "/v1/admin/gtcc/svc";

export const SVC_SERVICES_PATH = `${SVC_BASE}/services`;
export const SVC_DELIVERY_WATCHDOG_PATH = `${SVC_BASE}/delivery-watchdog`;
export const RULES_SUMMARY_PATH = "/v1/admin/gtcc/rules/summary";

/* ---------------------------------------------------------------- types */

export interface SvcServiceDto {
  readonly name: string;
  readonly host: string;
  readonly status: GtccStatus;
}

export interface SvcServicesDto extends GtccMeasured {
  /** `null` when the unit list itself could not be read. */
  readonly running: number | null;
  readonly total: number | null;
  readonly services: ReadonlyArray<SvcServiceDto>;
}

/**
 * The two delivery channels of the reference row `Mail / Telegram`. `label` is
 * the word the row prints (`Healthy`); the view never translates a status into
 * a health word of its own.
 */
export interface SvcChannelDto {
  readonly status: GtccStatus;
  readonly label: string;
}

export interface SvcDeliveryWatchdogDto extends GtccMeasured {
  readonly mail: SvcChannelDto;
  readonly telegram: SvcChannelDto;
  readonly rows: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly status: GtccStatus;
    readonly valueText: string;
    readonly reason?: string;
  }>;
  readonly mailDelivered7d: number | null;
  readonly bounces7d: number | null;
  readonly spf: string;
  readonly dkim: string;
  readonly dmarc: string;
  readonly telegramLatencyMs: number | null;
  readonly lastOwnerAlertAt: string | null;
  readonly syntheticTestAlert: {
    readonly schedule: string;
    readonly lastAt: string | null;
    readonly markedTest: boolean;
  };
  readonly reviews: { readonly weekly: string; readonly monthly: string };
  readonly routing: ReadonlyArray<{
    readonly severity: string;
    readonly telegram: string;
    readonly email: string;
    readonly language: string;
  }>;
}

/**
 * One firing alert family, grouped by `alertname`. The Overview uses these
 * groups twice: the crit families give the CRITICAL ALERTS status line
 * (`<Incident> — seit N min`), and — because the repository has no incident
 * module of its own — their count is ACTIVE INCIDENTS.
 */
export interface RulesAlertFamilyDto {
  readonly alertname: string;
  /** Owner-readable name of the family, e.g. `Offsite-Kette`. */
  readonly label: string;
  readonly severity: "critical" | "warning" | "info";
  readonly firing: number;
  /** `seit N min` as measured upstream; `null` when the start is unknown. */
  readonly firingSinceMinutes: number | null;
}

export interface RulesSummaryDto extends GtccMeasured {
  readonly firingCritical: number | null;
  readonly firingWarning: number | null;
  readonly families: ReadonlyArray<RulesAlertFamilyDto>;
}

/* -------------------------------------------------------------- fetchers */

export function fetchSvcServices(signal?: AbortSignal): Promise<GtccResult<SvcServicesDto>> {
  return gtccGet<SvcServicesDto>(SVC_SERVICES_PATH, signal);
}

export function fetchSvcDeliveryWatchdog(
  signal?: AbortSignal,
): Promise<GtccResult<SvcDeliveryWatchdogDto>> {
  return gtccGet<SvcDeliveryWatchdogDto>(SVC_DELIVERY_WATCHDOG_PATH, signal);
}

export function fetchRulesSummary(signal?: AbortSignal): Promise<GtccResult<RulesSummaryDto>> {
  return gtccGet<RulesSummaryDto>(RULES_SUMMARY_PATH, signal);
}

/* ----------------------------------------------------------- query keys */

/* Own array literals for the reason documented in `./ops.ts`. */
export const SVC_SERVICES_KEY = ["gtcc", "overview", "svc-services"] as const;
export const SVC_DELIVERY_WATCHDOG_KEY = ["gtcc", "overview", "svc-delivery-watchdog"] as const;
export const RULES_SUMMARY_KEY = ["gtcc", "overview", "rules-summary"] as const;

export const SVC_QUERY_KEYS = {
  services: SVC_SERVICES_KEY,
  deliveryWatchdog: SVC_DELIVERY_WATCHDOG_KEY,
  rulesSummary: RULES_SUMMARY_KEY,
} as const;

export type { GtccResult, GtccStatus, GtccVerdict };
